import React, { useState } from "react";
import {
  Button,
  Typography,
  Stack,
  Divider,
  Popover,
  MenuItem,
  Box,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import Treeview from "./Treeview";
import { useLocation, useParams } from "react-router-dom";
import FolderIcon from "@mui/icons-material/Folder";
import FolderCopyIcon from "@mui/icons-material/FolderCopy";
import { CollectionIcon } from "assets/icons/CollectionsIcon";
import { useSelector } from "react-redux";
import SideNavLink from "components/base/SideNavLink";
import { useDispatch } from "react-redux";
import { setIsBookmarkSidebar, setSubPopup } from "context/features/dataSlice";
import { useEffect } from "react";
import { toast } from "react-toastify";
import { SubmissionFolderDialog, SubmissionFolderNav } from "features/Submissions/components";
import { SUBMISSION_FOLDERS_UPDATED_EVENT } from "features/Submissions/hooks";
import { createSubmissionFolder } from "features/Submissions/services";

export default function SideMenu({ setNewPopup }) {
  // const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentLevel, setCurrentLevel] = useState(0);
  const [currentCollection, setCurrentCollection] = useState("");
  const [archivesExpanded, setArchivesExpanded] = useState(false);
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const { collections, collectionIdArray, archivedCollections } = useSelector(
    (state) => state.data
  );
  const { groupRole, groupId, gradeLicenseLevel } = useSelector((state) => state.account);
  const canManageSubmissions = groupId != null && (
    ["Teacher", "OrganizationAdmin", "Administrator", "Admin"].includes(groupRole) ||
    gradeLicenseLevel === "group-educator"
  );

  const dispatch = useDispatch();

  const theme = useTheme();

  const location = useLocation();
  const { pathname } = location;
  const params = useParams();
  // A teacher reviewing a submission is at /collections/:id/teacher-submission/:submissionId,
  // which is still the Submissions tab as far as the sidebar is concerned. Explore is
  // shared by both tabs, so which one it belongs to comes from where the link was clicked.
  const isSubmissionsPath =
    pathname.startsWith("/collections/submissions") ||
    pathname.includes("/teacher-submission") ||
    (pathname.startsWith("/collections/public") && Boolean(location.state?.fromSubmissionsTab));

  // The tabs must not wrap or clip when the sidebar is narrow, so let the row scroll.
  const parallelTabsSx = {
    ml: 1,
    mt: 3,
    mb: 1,
    pr: 3,
    // Keeps the active tab's underline clear of the scrollbar track below it.
    pb: 0.5,
    flexShrink: 0,
    whiteSpace: "nowrap",
    overflowX: "auto",
    "&::-webkit-scrollbar": { height: "6px" },
    "&::-webkit-scrollbar-track": { background: "transparent" },
    "&::-webkit-scrollbar-thumb": {
      background: theme.palette.grey[500],
      borderRadius: "3px",
    },
  };

  const tabStyle = (active) => ({
    color: theme.palette.text.primary,
    fontStyle: "italic",
    padding: "0 0 0.55rem",
    textDecoration: "none",
    borderBottom: `2px solid ${active ? theme.palette.primary.main : "transparent"}`,
  });

  const checkRecentAndPublic = () => {
    if (pathname.includes("/collections/public")) {
      return true;
    }
    if (params.id) {
      return false;
    }
    return true;
  };

  const handlePopoverOpen = (event) => {
    if (isSubmissionsPath) {
      setCreateFolderOpen(true);
      return;
    }
    if (checkRecentAndPublic()) {
      setNewPopup(true);
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const saveSubmissionFolder = async (payload) => {
    setCreatingFolder(true);
    try {
      await createSubmissionFolder(payload);
      toast.success("Submission folder created.");
      setCreateFolderOpen(false);
      window.dispatchEvent(new Event(SUBMISSION_FOLDERS_UPDATED_EVENT));
    } catch (requestError) {
      toast.error(requestError.message || "Unable to save submission folder.");
    } finally {
      setCreatingFolder(false);
    }
  };

  // const { isBookmarkSidebar } = useSelector((state) => state.data);

  const isPopoverOpen = Boolean(anchorEl);

  useEffect(() => {
    if (params.id) {
      const currentCollection = collections.filter(
        (collection) => Number(collection.collectionId) === Number(params.id)
      )[0];
      setCurrentCollection(currentCollection);
    }
  }, [params.id]);

  useEffect(() => {
    if (pathname.includes("/collections/archive")) {
      setArchivesExpanded(true);
    }
  }, [pathname]);

  return (
    <React.Fragment>
      <Box sx={{ ml: 3, pt: 2 }}>
        <Stack gap={1}>
          <Button
            size="large"
            startIcon={<AddIcon />}
            onClick={handlePopoverOpen}
            sx={{
              justifyContent: "flex-start",
              fontSize: "2.3rem",
              lineHeight: "3rem",
              mr: 3,
            }}
          >
            New
          </Button>
          <Popover
            anchorEl={anchorEl}
            open={isPopoverOpen}
            onClose={handlePopoverClose}
            anchorOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            sx={{
              transform: `translateY(10%)`,
            }}
            disableScrollLock={true}
          >
            <MenuItem
              sx={{
                display: "flex",
                gap: 2,
                p: 2,
                mt: 2,
                "&:hover": {
                  backgroundColor: theme.palette.secondary.main,
                },
              }}
              onClick={() => {
                setNewPopup(true);
                handlePopoverClose();
              }}
            >
              <FolderIcon sx={{ color: "primary.main" }} />
              <Typography variant="body1">New Collection</Typography>
            </MenuItem>
            {currentCollection != null && collectionIdArray.length < 3 && (
              <MenuItem
                disabled={currentLevel === 2 ? true : false}
                sx={{
                  display: "flex",
                  gap: 2,
                  p: 2,
                  "&:hover": {
                    backgroundColor: theme.palette.secondary.main,
                  },
                }}
                onClick={() => {
                  dispatch(setSubPopup(true));
                  handlePopoverClose();
                }}
              >
                <FolderCopyIcon sx={{ color: "primary.main" }} />

                <Typography variant="body1">
                  {`Subfolder in ${currentCollection.name}`}
                </Typography>
              </MenuItem>
            )}
            <MenuItem
              sx={{
                display: "flex",
                gap: 2,
                p: 2,
                mb: 2,
                "&:hover": {
                  backgroundColor: theme.palette.secondary.main,
                },
              }}
              onClick={() => {
                // setNewBookmarkPopup(true);
                handlePopoverClose();
                dispatch(setIsBookmarkSidebar(true));
              }}
            >
              <CollectionIcon sx={{
                  width: 20,
                  height: 20,
                  color: "primary.main",
              }}/>
              <Typography variant="body1">New External Bookmark</Typography>
            </MenuItem>
          </Popover>
          <SubmissionFolderDialog
            open={createFolderOpen}
            folder={null}
            saving={creatingFolder}
            onClose={() => setCreateFolderOpen(false)}
            onSubmit={saveSubmissionFolder}
          />
          {canManageSubmissions && (
            <Stack direction="row" gap={3} sx={{ ...parallelTabsSx }}>
              <SideNavLink
                to="/collections"
                end={false}
                className="parallel-nav-tab"
                style={() => tabStyle(!isSubmissionsPath)}
              >
                Collections
              </SideNavLink>
              <SideNavLink
                to="/collections/submissions"
                end={false}
                className="parallel-nav-tab"
                style={() => tabStyle(isSubmissionsPath)}
              >
                Submissions
              </SideNavLink>
            </Stack>
          )}
          {isSubmissionsPath && canManageSubmissions ? (
            <React.Fragment>
              <SubmissionFolderNav />
              <Divider sx={{ width: "90%" }} />
              <Typography variant="h6" color="primary.dark" fontStyle="italic">
                Public Collections
              </Typography>
              <SideNavLink to="/collections/public" state={{ fromSubmissionsTab: true }}>
                Explore
              </SideNavLink>
            </React.Fragment>
          ) : (
            <React.Fragment>
              <SideNavLink to="/collections">Recent Activity</SideNavLink>
              <Treeview
                collections={collections}
                collectionIdArray={collectionIdArray}
                setCurrentLevel={setCurrentLevel}
                maxHeight={archivedCollections.length > 0 ? "35vh" : "50vh"}
              />
              <Divider sx={{ width: "90%" }} />
              <Typography variant="h6" color="primary.dark" fontStyle="italic">
                Public Collections
              </Typography>
              <SideNavLink to="/collections/public">Explore</SideNavLink>
              {/* Archiving is available to every user in the regular collection view,
                  so the Archives group is always here — the same way the Submissions
                  nav always shows its own Archives group. */}
              <Divider sx={{ width: "90%" }} />
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                sx={{ pr: 3, cursor: "pointer" }}
                onClick={() => setArchivesExpanded((prev) => !prev)}
              >
                {archivesExpanded ? (
                  <ExpandMoreIcon sx={{ color: "primary.main" }} />
                ) : (
                  <ChevronRightIcon sx={{ color: "primary.main" }} />
                )}
                <ArchiveOutlinedIcon
                  sx={{ color: "primary.main", width: 18, height: 18 }}
                />
                <Typography variant="h6" color="primary.dark" fontStyle="italic">
                  Archives
                </Typography>
              </Stack>
              {archivesExpanded &&
                (archivedCollections.length > 0 ? (
                  <Treeview
                    collections={archivedCollections}
                    collectionIdArray={[]}
                    setCurrentLevel={() => {}}
                    basePath="/collections/archive"
                    maxHeight="20vh"
                  />
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ pl: 4 }}
                  >
                    No archived collections
                  </Typography>
                ))}
            </React.Fragment>
          )}
        </Stack>
      </Box>
    </React.Fragment>
  );
}
